<template>
  <div v-if="showOverlay" class="product-sticky-bar__overlay" @click="toggleShowOptions(false)"></div>
  <!-- Sticky Bar PDP -->
  <div id="sticky-bar" class="product-sticky-bar" :style="{ zIndex: showOptions ? '3' : '2' }" role="region">
    <!-- Closed -->
    <div class="product-sticky-bar__closed-label" v-if="!showOptions">
      <div class="product-sticky-bar__product-info">
        <ImageField
          :key="productImage"
          class="product-sticky-bar__product-image"
          :obj="{ url: productImage, alt: 'Alt Text', width: 264, height: 264 }"
          width-param="width"
          sizes="264px"
          fit="contain"
        />
        <div class="product-sticky-bar__product-inner">
          <span class="product-sticky-bar__product-title"> {{ product.title }} </span>
          <ProductPrice :product="product" :selectedFirstOption="selectedFirstLevelOption" />
        </div>
      </div>

      <div class="product-sticky-bar__right">
        <ProductATC
          v-if="productForm.selectedVariant"
          :product="product"
          :productForm="productForm"
          :selectedVariant="productForm.selectedVariant"
          :selectedFirstOption="selectedFirstLevelOption"
          :isPDPstickyBar="true"
          @product-added="toggleShowOptions(false)"
        />

        <button
          v-else
          class="btn product-sticky-bar__select-options-btn"
          @click="toggleShowOptions(true)"
        >
          Select options
        </button>
      </div>
    </div>

    <!-- Open -->
    <div
      v-else
      :class="['product-sticky-bar__wrapper', { 'product-sticky-bar__wrapper--open': showOptions }]"
      v-focus-trap="showOverlay && showOptions"
    >
      <button
        v-if="showOptions"
        class="product-sticky-bar__close"
        aria-label="close"
        @click="toggleShowOptions(false)"
        @keydown="handleKeydown"
        tabindex="0"
        role="button"
      >
        <svg class="product-sticky-bar__close-icon" aria-hidden="true">
          <use href="#close"></use>
        </svg>
      </button>
      <div class="product-sticky-bar__outer">
        <div class="product-sticky-bar__left">
          <BuyboxHeader
            :product="product"
            :productForm="productForm"
            :showWishlistCTA="false"
            :isSecondTurn="false"
            :showDescription="false"
            :disableReviewLink="true"
          />
          <ImageGallery
            v-if="showOptions && windowWidth > 1023"
            :product="product"
            :slidesPerView="windowWidth < 1367 ? 1.5 : 2.3"
            :showSlidesCount="false"
            :disableZoom="true"
            :slidesOffsetAfter="0"
          />
        </div>
        <div class="product-sticky-bar__right">
          <ProductPrice v-if="showOptions" :product="product" :selectedFirstOption="selectedFirstLevelOption" />
          <ProductOptions
            v-if="showOptions"
            :key="product"
            :product="product"
            :selectedOptions="productForm"
            :isSecondTurn="false"
            @on-option-change="onOptionChange"
          />
          <div data-sticky-cta v-if="productForm.selectedVariant || showOptions">
            <ProductATC
              :product="product"
              :productForm="productForm"
              :selectedVariant="productForm.selectedVariant"
              :selectedFirstOption="selectedFirstLevelOption"
              :isPDPstickyBar="true"
              @product-added="toggleShowOptions(false)"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
  import { computed, onMounted, reactive, ref, watch } from 'vue';
  import { useStore } from 'vuex';
  import ImageField from '../../global/image.vue';
  import ProductPrice from './product-price.vue';
  import ProductATC from './product-atc.vue';
  import ProductOptions from '../../product-options/index.vue';
  import ImageGallery from './image-gallery.vue';
  import BuyboxHeader from './buybox-header.vue';

  const props = defineProps({
    product: {
      type: Object,
      default: () => ({}),
    },
  });

  const store = useStore();
  const product = reactive(props.product);
  const productForm = reactive(store.state.pdp.productForm);
  const showOptions = ref(false);
  const showOverlay = ref(false);
  const windowWidth = ref(window.innerWidth);

  const selectedFirstLevelOption = computed(() => store.state.pdp.firstLevelOptions?.[product.id]);

  const productImage = computed(() => {
    const selectedAlt = store.state.pdp.firstLevelOptions?.[product.id]?.value;
    const matchedImage = product.images.find((image) => image.alt === selectedAlt);
    return matchedImage?.src || product.images[0]?.src;
  });

  onMounted(() => {
    window.addEventListener('resize', () => {
      windowWidth.value = window.innerWidth;
    });
  });

  const toggleShowOptions = (value) => {
    showOptions.value = value;
    if (value) {
      showOverlay.value = true;
      document.body.style.overflow = 'hidden';
      window.refreshYotpoData();
    } else {
      showOverlay.value = false;
      document.body.style.overflow = 'auto';
    }
  };

  const handleKeydown = (event) => {
    const keysToClose = ['Enter', ' ', 'Spacebar']; // Spacebar is for older browsers
    if (keysToClose.includes(event.key)) {
      event.preventDefault(); // Prevent default behavior for Space
      toggleShowOptions(false);
    }
  };

  const onOptionChange = (key, value, index, length) => {
    updateProductForm(key, value);
    updateVariantValue();
  };

  const updateVariantValue = () => {
    const { options } = product;
    const variantValue = options.map((option) => productForm[option.name]).join(' / ');
    updateSelectedVariant(variantValue);
  };

  const updateSelectedVariant = (value) => {
    const variant = product.variants.find((item) => item.value === value);
    const selectedVariant = variant || null;
    updateProductForm('selectedVariant', selectedVariant);
    updateParams('update', productForm.selectedVariant);
  };

  const updateProductForm = (key, value) => {
    store.dispatch('updatePDPProductForm', {
      key: key,
      value: value,
    });
  };

  const updateParams = (type, variant) => {
    const url = new URL(window.location.href);
    const params = new URLSearchParams(url.search);

    if (type === 'update' && variant) {
      params.set('variant', variant.id);
    } else if (type === 'delete') {
      params.delete('color');
    }

    url.search = params.toString();
    window.history.replaceState({ url: url.toString() }, null, url.toString());
  };

  watch(showOptions, () => {
    const buyboxEl = document.querySelector('.buybox');
    if (!buyboxEl) return;

    if (showOptions.value) {      
      const currentHeight = buyboxEl.scrollHeight;
      buyboxEl.style.height = `${currentHeight}px`;
      buyboxEl.style.overflow = 'hidden';
    } else {
      buyboxEl.style.height = '';
      buyboxEl.style.overflow = '';
    }
  });
</script>
